/**
 * "Scan on an edge agent" (Db2 for i Phase 2).
 *
 * Replaces the old Edge pod branch of the Scans wizard, which built a target
 * without an agent and so recorded jobs that never ran. The container loads the
 * fleet and submits; EdgeAgentScanFormView is pure so its gating is testable
 * with a static render. All target logic lives in @/lib/edge-scan-targets.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { AlertTriangle, Loader2, Play } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { enqueueScanJob, listEdgeFleet, type EdgePodSummary } from '@/lib/api';
import { edgeHealthLabel } from '@/lib/edge-health';
import {
  DB2I_DEFAULT_FORM,
  buildDb2ForITarget,
  db2iScanReadiness,
  type Db2ForIForm,
  type EdgeScanReadiness,
} from '@/lib/edge-scan-targets';

// Theme-aware utility that works in both light and dark mode, matching CrawlField in Scans.tsx
const inputCls = 'w-full glass-lite rounded-md px-3 py-2 text-sm text-foreground bg-transparent outline-none focus:ring-1 focus:ring-primary/40';

export interface EdgeAgentScanFormViewProps {
  pods: EdgePodSummary[];
  loading: boolean;
  selectedPodId: string;
  onSelectPod: (id: string) => void;
  form: Db2ForIForm;
  onChange: (patch: Partial<Db2ForIForm>) => void;
  readiness: EdgeScanReadiness;
  formError: string | null;
  submitting: boolean;
  submitError: string | null;
  onSubmit: () => void;
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</span>
      {children}
      {hint ? <span className="block text-[11px] text-muted-foreground">{hint}</span> : null}
    </label>
  );
}

export function EdgeAgentScanFormView(p: EdgeAgentScanFormViewProps) {
  const blocked = !p.readiness.ok;
  return (
    <div className="space-y-4">
      <Field label="Edge agent" hint="The agent that runs this scan. Only agents of the Discovery component are listed.">
        <select className={inputCls} value={p.selectedPodId} onChange={(e) => p.onSelectPod(e.target.value)} aria-label="Edge agent">
          <option value="">{p.loading ? 'Loading agents...' : 'Choose an agent'}</option>
          {p.pods.map((pod) => (
            <option key={pod.podId} value={pod.podId} className="bg-background text-foreground">
              {pod.displayName} ({edgeHealthLabel(pod.healthStatus)}, v{pod.version})
            </option>
          ))}
        </select>
      </Field>

      <Field label="Engine" hint="Other engines need credential references on the agent before they can be queued here.">
        <select className={inputCls} value="db2_for_i" aria-label="Engine" disabled>
          <option value="db2_for_i">Db2 for i (IBM i / AS/400)</option>
        </select>
      </Field>

      <div className="grid gap-3 md:grid-cols-2">
        <Field label="IBM i system">
          <input className={inputCls} value={p.form.system} onChange={(e) => p.onChange({ system: e.target.value })} placeholder="as400.example.internal" />
        </Field>
        <Field label="User profile" hint="Read-only; needs *USE on the libraries scanned.">
          <input className={inputCls} value={p.form.username} onChange={(e) => p.onChange({ username: e.target.value })} />
        </Field>
        <Field label="Credential name" hint="The password is a file of this name on the agent host (/run/secrets by default), or lives in Azure Key Vault, HashiCorp Vault or CyberArk when <name>.vault there binds it to a secret. The file <name>.systems next to it must list this IBM i system. Add profile=<PROFILE> to that entry to restrict it to this user profile, and plaintext=allow if TLS is off below. The password never passes through FOCTTA.">
          <input className={inputCls} value={p.form.credential} onChange={(e) => p.onChange({ credential: e.target.value })} placeholder="core-banking-ibmi" />
        </Field>
        <Field label="Scope">
          <select className={inputCls} value={p.form.scope} onChange={(e) => p.onChange({ scope: e.target.value as Db2ForIForm['scope'] })} aria-label="Scope">
            <option value="whole_system">Whole system (minus the deny list)</option>
            <option value="library">One library</option>
            <option value="ifs">IFS directory (stream files)</option>
          </select>
        </Field>
        {p.form.scope === 'library' ? (
          <Field label="Library">
            <input className={inputCls} value={p.form.library} onChange={(e) => p.onChange({ library: e.target.value })} placeholder="CUSTLIB" />
          </Field>
        ) : p.form.scope === 'ifs' ? (
          <Field label="IFS directory" hint="Text files (CSV, TXT, JSON, XML, logs) under this directory and below are read, converted from their EBCDIC CCSID by the IBM i. PDF and Office files are not read yet and are reported as skipped. Needs IBM i 7.3 or later and *RX on every directory in the tree: a folder the scanning profile cannot open is left out of the listing, and is counted as an unreadable folder where the scan can tell.">
            <input className={inputCls} value={p.form.ifsPath} onChange={(e) => p.onChange({ ifsPath: e.target.value })} placeholder="/data/extracts" />
          </Field>
        ) : (
          <Field label="Also skip libraries" hint="Comma separated, for example archive and test libraries. The IBM product libraries (QSYS, QSYS2, QGPL, ...) are always skipped.">
            <input className={inputCls} value={p.form.libraryDenylist} onChange={(e) => p.onChange({ libraryDenylist: e.target.value })} />
          </Field>
        )}
        <Field label="IASP" hint="Leave empty for *SYSBAS.">
          <input className={inputCls} value={p.form.iasp} onChange={(e) => p.onChange({ iasp: e.target.value })} />
        </Field>
        <Field label="CCSID for *HEX fields" hint="37 is common; 500 and 1140 are the other frequent ones.">
          <input className={inputCls} value={p.form.hexFieldCcsid} onChange={(e) => p.onChange({ hexFieldCcsid: e.target.value })} inputMode="numeric" />
        </Field>
        <Field label="ODBC driver name" hint="Leave empty for the IBM i Access ODBC Driver. The driver is staged on the agent host by you.">
          <input className={inputCls} value={p.form.driverName} onChange={(e) => p.onChange({ driverName: e.target.value })} />
        </Field>
      </div>

      <div className="space-y-2 text-sm">
        <label className="flex items-start gap-2">
          <input type="checkbox" checked={p.form.useTls} onChange={(e) => p.onChange({ useTls: e.target.checked })} />
          <span>
            Encrypt the connection (TLS)
            <span className="block text-[11px] text-muted-foreground">
              Needs a certificate on the *DATABASE host server in Digital Certificate Manager. Turn off only where that is not in place and the network path is protected.
            </span>
          </span>
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={p.form.includeSourceFiles} onChange={(e) => p.onChange({ includeSourceFiles: e.target.checked })} />
          Include source physical files
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={p.form.includeLogicalFiles} onChange={(e) => p.onChange({ includeLogicalFiles: e.target.checked })} />
          Include logical files and views
        </label>
      </div>

      {!p.readiness.ok ? (
        <p className="flex items-start gap-2 text-xs text-destructive"><AlertTriangle className="h-4 w-4 shrink-0" />{p.readiness.reason}</p>
      ) : p.readiness.warning ? (
        <p className="flex items-start gap-2 text-xs text-amber-600 dark:text-amber-300"><AlertTriangle className="h-4 w-4 shrink-0" />{p.readiness.warning}</p>
      ) : null}
      {p.formError ? <p className="text-xs text-destructive">{p.formError}</p> : null}
      {p.submitError ? <p className="text-xs text-destructive">{p.submitError}</p> : null}

      <Button onClick={p.onSubmit} disabled={blocked || p.submitting} className="gap-1.5" data-action="disc.dspm_scans.a04">
        {p.submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
        Queue scan
      </Button>
    </div>
  );
}

export function EdgeAgentScanForm({ onQueued }: { onQueued: () => void }) {
  const [pods, setPods] = useState<EdgePodSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPodId, setSelectedPodId] = useState('');
  const [form, setForm] = useState<Db2ForIForm>(DB2I_DEFAULT_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    listEdgeFleet({ component: 'discovery' })
      .then((r) => setPods(r.pods.filter((p) => p.healthStatus !== 'decommissioned')))
      .catch((e: unknown) => setSubmitError(e instanceof Error ? `Could not load agents: ${e.message}` : 'Could not load agents.'))
      .finally(() => setLoading(false));
  }, []);

  const pod = useMemo(() => pods.find((p) => p.podId === selectedPodId) ?? null, [pods, selectedPodId]);
  const readiness = useMemo(() => db2iScanReadiness(pod, form.credential, form.scope), [pod, form.credential, form.scope]);

  async function onSubmit() {
    setFormError(null);
    setSubmitError(null);
    const built = buildDb2ForITarget(form);
    if (!built.ok) {
      setFormError(built.error);
      return;
    }
    if (!pod) return;
    setSubmitting(true);
    try {
      await enqueueScanJob({ target: built.target, podId: pod.podId, scanMode: 'full' });
      onQueued();
    } catch (e: unknown) {
      setSubmitError(e instanceof Error ? e.message : 'Failed to queue the scan.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <EdgeAgentScanFormView
      pods={pods} loading={loading} selectedPodId={selectedPodId} onSelectPod={setSelectedPodId}
      form={form} onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
      readiness={readiness} formError={formError} submitting={submitting} submitError={submitError}
      onSubmit={() => void onSubmit()}
    />
  );
}
